// ─────────────────────────────────────────────────────────────────────────────
// Scripts de marketing de terceros (GTM, que administra Meta Pixel) + el widget de soporte
// (Intercom), FUERA del camino crítico del boot.
//
// El problema: en /agenda —la herramienta de trabajo diaria— estos scripts eran
// ~490 KB de los ~570 KB de terceros que bajaban ANTES de que el vendor viera su
// primer turno, y le competían la CPU a React en un celular de gama baja. El píxel
// de Facebook no ayuda a nadie a ver sus turnos.
//
// La restricción: DIFERIR, NO ELIMINAR. GTM y el píxel alimentan la atribución de
// signups y las conversiones de Meta/Google Ads. Si un evento de conversión deja
// de dispararse, el fix es un fracaso aunque la agenda cargue en 0 ms.
//
// Lo que lo hace seguro — el diseño en DOS FASES:
//
//   Fase 1 · installThirdPartyStubs() — SINCRÓNICA, 0 bytes de red, ~0 CPU.
//     Crea `window.dataLayer`, el stub de `fbq` y una cola propia para los eventos
//     de Ágora. GTM es el dueño del único `fbq('init')`: cuando el stub lo observa,
//     pone detrás los eventos pendientes. Así el init siempre se ejecuta ANTES de
//     cualquier track sin inicializar dos veces el mismo píxel.
//
//   Fase 2 · loadMarketingScripts() — baja GTM. El contenedor carga una única
//     instancia de Meta Pixel y ambos scripts drenan sus respectivas colas.
//
// CUÁNDO corre la fase 2 depende de la RUTA:
//   • Rutas de conversión (auth, onboarding, facturación) → YA, sin esperar. Son
//     las que disparan Lead / CompleteRegistration / purchase Y las que pueden
//     irse a otro origen de una (Auth0, Stripe/Boxful). Una cola que no alcanza a
//     drenarse antes del unload es una conversión PERDIDA — ahí no se especula.
//   • El resto (agenda, clientes, caja…) → recién cuando el main thread se calma.
//     Esas rutas no disparan NINGÚN evento de fbq ni de dataLayer (ver los únicos
//     call sites: Auth0Callback, SelfOnboarding, PlanSelection, CampaignLanding,
//     CompraExitosa), así que no hay literalmente nada que perder.
//
// Y como red de seguridad extra, cualquier helper de conversión puede llamar a
// flushThirdPartyScripts() para forzar la fase 2 en el momento.
// ─────────────────────────────────────────────────────────────────────────────

const GTM_ID = 'GTM-5W6HFVH';
const META_PIXEL_ID = '750381646381609';

/** Main thread sin long tasks por este rato ⇒ la app ya pintó y podemos cargar. */
const QUIET_MS = 1000;
/** Si el hilo nunca se calma, cargamos igual: analytics no puede quedar colgado. */
const HARD_CAP_MS = 12000;
const SENSITIVE_QUERY_PARAMS = ['first_name', 'last_name', 'email', 'phone'] as const;

type FbqFn = ((...args: unknown[]) => void) & {
  queue: unknown[];
  callMethod?: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
  push: unknown;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    fbq?: FbqFn;
    _fbq?: FbqFn;
    /** true ⇒ es seguro llamar a `fbq('track', …)`: el init ya está encolado. */
    _fbqInitialized?: boolean;
    /** Loader del widget de Intercom, expuesto por el snippet de index.html. */
    __loadIntercom?: () => void;
  }
}

const isProd = (): boolean => import.meta.env.VITE_NODE_ENV === 'production';

/**
 * Rutas donde OCURRE la conversión y/o el documento puede navegar a otro origen
 * (Auth0, Stripe/Boxful) antes de que un callback diferido llegue a correr.
 */
const CONVERSION_ROUTES = [
  '/login',
  '/registro',
  '/comenzar', // landings de campañas pagas (/comenzar y /comenzar-up) → fbq PageView + signup
  '/callback', // Auth0 → fbq CompleteRegistration
  '/new/user',
  '/onboarding', // SelfOnboarding → trackCustom `Step N`; PlanSelection → `Lead` / `Lead <rubro>`
  '/bienvenido',
  '/setup',
  '/facturacion', // → /facturacion/compra-exitosa → dataLayer `purchase`
  '/plans'
];

const isConversionRoute = (pathname: string): boolean =>
  CONVERSION_ROUTES.some((route) => pathname.startsWith(route));

let stubsInstalled = false;
let loadPromise: Promise<void> | null = null;
const pendingMetaEvents: unknown[][] = [];

const flushPendingMetaEvents = (): void => {
  if (!window._fbqInitialized || typeof window.fbq !== 'function') return;

  const events = pendingMetaEvents.splice(0);
  events.forEach((args) => window.fbq?.(...args));
};

/**
 * Meta y GTM adjuntan la URL actual a los eventos de página. Nunca deben recibir
 * PII que haya llegado accidentalmente en una query string. Conservamos intactos
 * fbclid, gclid y utm_* para no degradar la atribución de campañas.
 */
const stripSensitiveQueryParamsFromUrl = (): void => {
  const url = new URL(window.location.href);
  const hasSensitiveParams = SENSITIVE_QUERY_PARAMS.some((param) => url.searchParams.has(param));

  if (!hasSensitiveParams) return;

  SENSITIVE_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Fase 1. Idempotente. Después de esto, `fbq('track', …)` y `dataLayer.push(…)`
 * son seguros desde cualquier lado, hayan bajado o no los scripts pesados.
 */
export const installThirdPartyStubs = (): void => {
  if (stubsInstalled || typeof window === 'undefined' || !isProd()) return;
  stubsInstalled = true;

  stripSensitiveQueryParamsFromUrl();

  window.dataLayer = window.dataLayer || [];

  if (typeof window.fbq === 'undefined') {
    const fbq = function (...args: unknown[]) {
      // Una vez que fbevents.js cargó, define callMethod y pasamos derecho.
      // (llamarlo como método deja `this` = fbq, igual que el `.apply` del snippet)
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);

      // GTM administra el píxel y es la única fuente del init. Los tracks de la
      // app que llegaron antes se agregan inmediatamente detrás de ese init.
      if (args[0] === 'init' && args[1] === META_PIXEL_ID) {
        window._fbqInitialized = true;
        flushPendingMetaEvents();
      }
    } as FbqFn;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
  }
};

/** Fase 2. Idempotente: baja GTM, que carga Meta Pixel y drena ambas colas. */
const loadMarketingScripts = (): Promise<void> => {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    // GTM es la única fuente de fbevents.js. Cargar Meta también desde acá hacía
    // que el tag del contenedor instalara una segunda copia y enviara cada Lead
    // dos veces. Su snippet respeta tanto la dataLayer que veníamos llenando como
    // el stub de fbq ya instalado; al cargar, drena ambas colas en orden (incl. el
    // `init`, cualquier conversión encolada y el `purchase` de CompraExitosa).
    try {
      const TagManager = await import('react-gtm-module');
      TagManager?.default?.initialize({ gtmId: GTM_ID });
    } catch (e) {
      // Sesión con bundle viejo tras un deploy: el chunk ya no existe en el CDN y el
      // import rechaza. Es solo analytics — no puede tirar la app ni paginar PostHog.
      // Se resetea el memo para que un próximo flush (o la sesión fresca post-reload)
      // reintente en lugar de cachear el rechazo para siempre.
      loadPromise = null;
      console.warn('[thirdParty] no se pudo cargar GTM (chunk viejo tras deploy?)', e);
    }
  })();

  return loadPromise;
};

/**
 * Fuerza la fase 2 en el momento. La llaman los helpers de conversión: si un
 * evento se dispara desde una ruta diferida, el script se baja ahí mismo y la
 * cola se drena igual. Idempotente y barato.
 */
export const flushThirdPartyScripts = (): void => {
  if (typeof window === 'undefined' || !isProd()) return;
  installThirdPartyStubs();
  void loadMarketingScripts();
};

/**
 * Encola un evento propio detrás del init que administra GTM. Esto evita tanto
 * perder conversiones durante el boot como inicializar dos veces el mismo píxel.
 */
export const trackMetaPixelEvent = (...args: unknown[]): void => {
  flushThirdPartyScripts();
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;

  if (window._fbqInitialized) window.fbq(...args);
  else pendingMetaEvents.push(args);
};

/**
 * Corre `cb` cuando el main thread lleva QUIET_MS sin long tasks (⇒ la app ya
 * pintó su contenido), o a los HARD_CAP_MS pase lo que pase.
 *
 * Por qué no un `requestIdleCallback` pelado: durante el boot el hilo SÍ queda
 * libre mientras espera la API, así que el idle dispara a los ~800 ms — justo a
 * tiempo para que el script pesado ejecute encima del primer render. Esperar a
 * que se calme de verdad es lo que lo saca del camino crítico.
 */
const whenMainThreadQuiet = (cb: () => void): void => {
  let fired = false;
  let quietTimer: number | undefined;
  let observer: PerformanceObserver | null = null;

  const fire = () => {
    if (fired) return;
    fired = true;
    window.clearTimeout(quietTimer);
    observer?.disconnect();
    cb();
  };

  window.setTimeout(fire, HARD_CAP_MS);

  const start = () => {
    if (fired) return;
    quietTimer = window.setTimeout(fire, QUIET_MS);
    try {
      observer = new PerformanceObserver(() => {
        // Hubo una long task: la app sigue laburando → volvemos a esperar.
        window.clearTimeout(quietTimer);
        quietTimer = window.setTimeout(fire, QUIET_MS);
      });
      observer.observe({ type: 'longtask', buffered: false });
    } catch {
      // Navegador sin longtask (Safari viejo): alcanza con el timer de arriba.
    }
  };

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
};

/**
 * Punto de entrada del boot (App.tsx). Instala las colas SIEMPRE; decide cuándo
 * bajar los ~230 KB de GTM+Pixel según la ruta de aterrizaje, y manda a Intercom
 * (~260 KB, soporte, nunca una conversión) siempre al final de la fila.
 */
export const scheduleThirdPartyScripts = (): void => {
  if (typeof window === 'undefined' || !isProd()) return;

  installThirdPartyStubs();

  const marketingIsUrgent = isConversionRoute(window.location.pathname);
  if (marketingIsUrgent) void loadMarketingScripts();

  whenMainThreadQuiet(() => {
    if (!marketingIsUrgent) void loadMarketingScripts();
    // El botón de soporte sigue apareciendo — solo deja de pelear por la CPU
    // mientras la agenda pinta. El snippet de index.html tiene su propia red de
    // seguridad por si el bundle nunca bootea.
    window.__loadIntercom?.();
  });
};
